import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Bell, Shirt, Sparkles } from "lucide-react";
import { db } from "../firebase";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { mergeSiteContent } from "../utils/helpers";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import {
  DEFAULT_LATDIOR_PRODUCTS,
  LATDIOR_OFFICIAL_URL,
  formatCurrency,
  normalizeLatdiorProduct,
} from "../lib/platformData";
import { createWaitlistRequest } from "../lib/firestoreActions";
import MediaRenderer from "../components/MediaRenderer";
import { useToast } from "../context/ToastContext";
import { typographyFor } from "../lib/typography";

const fallbackProducts = [
  "/images/latdior1.png",
  "/images/latdior2.png",
  "/images/latdior3.png",
  "/images/latdior4.png",
  "/images/latdior5.png",
];

const drops = [
  { label: "Drop", key: "drop" },
  { label: "Mood", key: "mood" },
  { label: "Statut", key: "status" },
];

export default function Latdior() {
  const [merch, setMerch] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [site, setSite] = useState(() => mergeSiteContent());
  const [sizes, setSizes] = useState({});
  const [waitlistEmails, setWaitlistEmails] = useState({});
  const [feedback, setFeedback] = useState({});
  const { user } = useAuth();
  const { addItem } = useCart();
  const toast = useToast();
  const [waitlistBusy, setWaitlistBusy] = useState({});

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setSite(mergeSiteContent(data));
        if (Array.isArray(data.merch)) setMerch(data.merch.filter(Boolean));
      },
      (err) => {
        console.error("Erreur Firestore Latdior:", err);
      }
    );
    return () => off();
  }, []);

  useEffect(() => {
    const off = onSnapshot(
      collection(db, "latdiorProducts"),
      (snap) => {
        setCatalog(
          snap.docs
            .map((item) => normalizeLatdiorProduct({ id: item.id, ...item.data() }))
            .filter((item) => item.name)
        );
      },
      () => setCatalog([])
    );
    return () => off();
  }, []);

  const page = site.pages.latdior;
  const ctaUrl = page.ctaUrl?.startsWith("mailto:") ? "#collection" : page.ctaUrl || "#collection";
  const products = useMemo(() => {
    if (catalog.length) return catalog;

    const pageProducts = Array.isArray(page.products) ? page.products.filter(Boolean) : [];
    const images = pageProducts.length ? pageProducts : merch.length ? merch : fallbackProducts;
    return images.map((image, index) => {
      const fallback = DEFAULT_LATDIOR_PRODUCTS[index] || {
        id: `latdior-piece-${index + 1}`,
        name: `Latdior Piece ${String(index + 1).padStart(2, "0")}`,
        description: "Edition capsule Latdior.",
        price: index === 0 ? 220 : 560,
        sizes: ["S", "M", "L", "XL"],
        available: index < 2,
      };
      return normalizeLatdiorProduct({ ...fallback, image, id: fallback.id || `latdior-${index}` });
    });
  }, [catalog, merch, page.products]);

  const addProduct = (product) => {
    const selectedSize = sizes[product.id] || product.sizes?.[0] || "";
    addItem({
      type: "latdior",
      productId: product.id,
      name: product.name,
      image: product.image,
      size: selectedSize,
      unitPrice: product.price,
      quantity: 1,
      currency: "EUR",
    });
    setFeedback((current) => ({ ...current, [product.id]: "Ajoute au panier." }));
  };

  const waitlist = async (product) => {
    setWaitlistBusy((current) => ({ ...current, [product.id]: true }));
    try {
      const result = await createWaitlistRequest({
        email: waitlistEmails[product.id] || user?.email,
        user,
        productId: product.id,
        productName: product.name,
      });
      const message = result.alreadyRegistered
        ? "Cette adresse est déjà inscrite pour ce produit."
        : result.emailSent
          ? "Demande enregistrée. Un email de confirmation vient de t'être envoyé."
          : "Demande enregistrée. L'email de confirmation est en cours d'envoi.";
      setFeedback((current) => ({ ...current, [product.id]: message }));
      setWaitlistEmails((current) => ({ ...current, [product.id]: "" }));
      toast.success(result.alreadyRegistered ? "Alerte déjà active" : "Alerte disponibilité enregistrée", result.emailSent ? "Confirmation envoyée." : "Confirmation en attente.");
    } catch (err) {
      setFeedback((current) => ({ ...current, [product.id]: err.message || "Email invalide." }));
      toast.error("Inscription impossible", err.message || "Vérifie ton adresse email.");
    } finally {
      setWaitlistBusy((current) => ({ ...current, [product.id]: false }));
    }
  };

  return (
    <main className="min-h-screen bg-black text-white">
      {site.visibility.latdiorHero && (
      <section className="relative overflow-hidden px-6 pb-16 pt-24 md:px-20 md:pt-28">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_15%,rgba(220,38,38,0.24),transparent_32%),linear-gradient(180deg,#090909,#000)]" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-[0.95fr_1.05fr]">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-red-500">
              <Shirt size={15} /> {page.badge}
            </p>
            <h1 className="text-6xl font-black uppercase leading-none md:text-8xl" style={typographyFor(site, "latdiorTitle")}>
              {page.title}
            </h1>
            <p className="readable-copy mt-6 max-w-xl text-lg text-gray-300" style={typographyFor(site, "latdiorText")}>
              {page.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={ctaUrl}
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-black transition hover:bg-red-600 hover:text-white"
              >
                {page.ctaLabel} <Bell size={16} />
              </a>
              <a
                href="#collection"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-wide transition hover:border-white hover:bg-white hover:text-black"
              >
                {page.secondaryLabel} <ArrowRight size={16} />
              </a>
              <a
                href={LATDIOR_OFFICIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-red-500/50 px-6 py-3 text-sm font-bold uppercase tracking-wide text-red-100 transition hover:bg-red-600 hover:text-white"
              >
                Site officiel <ArrowRight size={16} />
              </a>
            </div>
          </div>

          <div className="relative min-h-[430px] md:min-h-[620px]">
            {products.slice(0, 3).map((src, index) => (
              <MediaRenderer
                key={src.id || src.image}
                src={src.media || src.image}
                type={src.mediaType || "image"}
                alt={`Latdior piece ${index + 1}`}
                background
                className={`absolute rounded-lg border border-white/10 bg-zinc-950 object-cover shadow-2xl ${
                  index === 0
                    ? "left-0 top-10 h-[360px] w-[58%] md:h-[520px]"
                    : index === 1
                      ? "right-0 top-0 h-[270px] w-[46%] md:h-[380px]"
                      : "bottom-0 right-8 h-[220px] w-[44%] md:h-[310px]"
                }`}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {site.visibility.latdiorStats && (
      <section className="border-y border-white/10 bg-zinc-950 px-6 py-6 md:px-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 md:grid-cols-3">
          {drops.map((item) => (
            <div key={item.label} className="flex items-center justify-between border-white/10 md:border-r md:pr-8 last:border-r-0">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">{item.label}</span>
              <span className="text-sm font-semibold text-white">{page[item.key]}</span>
            </div>
          ))}
        </div>
      </section>
      )}

      {site.visibility.latdiorCollection && (
      <section id="collection" className="px-6 py-20 md:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
                Collection
              </p>
              <h2 className="text-4xl font-black uppercase md:text-6xl">
                Pieces du drop
              </h2>
            </div>
            <p className="readable-copy max-w-md text-gray-400">
              Une sélection de pièces conçues pour prolonger l’univers Unlimited Session.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((src, index) => (
              <article key={`${src.id}-${index}`} className="group overflow-hidden rounded-lg border border-white/10 bg-zinc-950">
                <div className="aspect-[4/5] overflow-hidden bg-zinc-900">
                  <MediaRenderer
                    src={src.media || src.image}
                    type={src.mediaType || "image"}
                    alt={src.name}
                    background
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    loading={index > 2 ? "lazy" : "eager"}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold">{src.name}</h3>
                      <p className="readable-copy mt-1 text-sm text-gray-500" style={typographyFor(site, "latdiorText")}>{src.description || "Edition capsule"}</p>
                    </div>
                    <Sparkles className="shrink-0 text-red-500" size={20} />
                  </div>
                  <div className="mt-5 flex items-center justify-between gap-4">
                    <p className="text-xl font-black">{formatCurrency(src.price)}</p>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${
                      src.available ? "bg-green-500/10 text-green-200" : "bg-red-500/10 text-red-200"
                    }`}>
                      {src.available ? "Disponible" : "Indisponible"}
                    </span>
                  </div>
                  <label className="mt-4 block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-500">Taille</span>
                    <select
                      value={sizes[src.id] || src.sizes?.[0] || ""}
                      onChange={(e) => setSizes((current) => ({ ...current, [src.id]: e.target.value }))}
                      className="w-full rounded border border-white/10 bg-black px-3 py-2 outline-none focus:border-red-500"
                    >
                      {(src.sizes || []).map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </label>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    {src.available ? (
                      <button
                        onClick={() => addProduct(src)}
                        className="rounded-full bg-white px-5 py-2 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white"
                      >
                        Ajouter au panier
                      </button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <input
                          type="email"
                          value={waitlistEmails[src.id] ?? user?.email ?? ""}
                          onChange={(e) => setWaitlistEmails((current) => ({ ...current, [src.id]: e.target.value }))}
                          placeholder="email@exemple.com"
                          className="rounded border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-red-500"
                        />
                        <button
                          onClick={() => waitlist(src)}
                          disabled={waitlistBusy[src.id]}
                          className="rounded-full border border-white/15 px-5 py-2 text-sm font-bold uppercase transition hover:border-white hover:bg-white hover:text-black disabled:cursor-wait disabled:opacity-60"
                        >
                          {waitlistBusy[src.id] ? "Inscription..." : "Prevenir quand disponible"}
                        </button>
                      </div>
                    )}
                    <a
                      href={src.externalUrl || LATDIOR_OFFICIAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-center text-xs font-bold uppercase tracking-wide text-gray-400 hover:text-white"
                    >
                      Voir plus sur Latdior Paris
                    </a>
                  </div>
                  {feedback[src.id] && <p className="mt-3 text-sm text-gray-300">{feedback[src.id]}</p>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      )}
    </main>
  );
}
